'use client';

import { Button } from '@common/components';
import { SvgMagnifyingGlass, SvgShoppingBasketFill } from '@common/icon';
import { cn } from '@core/utils';
import { usePathname } from 'next/navigation';

import { CustomLink } from '@/app/_components/CustomLink';
import { HeaderLogo } from '@/app/_components/HeaderLogo';
import {
  HeaderSearchContainer,
  useSearchOverlayStore,
} from '@/app/_components/SearchBar';
import { HeaderProfileAvatar } from '@/app/(allowed)/my/_components/HeaderProfileAvatar';
import { useMarketCart } from '@/app/welmarket/cart/_hooks/useMarketCart';
import { HIDE_DESKTOP_HEADER_PATHNAMES } from '@/components/template/GlobalLayout/constants';
import { DONATION_ROUTES, ROUTES, WEL_MARKET_ROUTES } from '@/constants';
import { SUB_DOMAIN } from '@/constants/domain';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { useAuthStore } from '@/stores/AuthStore';
import { useDeviceInfoStore } from '@/stores/DeviceStore';

interface DesktopNavItem {
  label: string;
  href: string;
  isActive: (pathname: string) => boolean;
}

const isSectionPath = (pathname: string, sectionRoot: string) =>
  pathname === sectionRoot || pathname.startsWith(`${sectionRoot}/`);

const DESKTOP_NAV_ITEMS: DesktopNavItem[] = [
  {
    label: '홈',
    href: ROUTES.HOME.pathname,
    isActive: (pathname) => pathname === ROUTES.HOME.pathname,
  },
  {
    label: '고향사랑기부',
    href: DONATION_ROUTES.HOME.pathname,
    isActive: (pathname) =>
      isSectionPath(pathname, DONATION_ROUTES.HOME.pathname),
  },
  {
    label: '웰마켙',
    href: WEL_MARKET_ROUTES.HOME.pathname,
    isActive: (pathname) =>
      isSectionPath(pathname, WEL_MARKET_ROUTES.HOME.pathname),
  },
];

export const DesktopHeader = () => {
  const isLogin = useAuthStore((state) => state.isLogin);
  const pathname = usePathname() ?? '';
  const subDomain = useDeviceInfoStore((state) => state.subDomain);
  const router = useCustomRouter();
  const isOverlayOpen = useSearchOverlayStore((state) => state.isOverlayOpen);
  const openOverlay = useSearchOverlayStore((state) => state.openOverlay);
  const closeOverlay = useSearchOverlayStore((state) => state.closeOverlay);

  const isHiddenPage = HIDE_DESKTOP_HEADER_PATHNAMES.includes(pathname);
  const isLocalMarketPath = isSectionPath(
    pathname,
    WEL_MARKET_ROUTES.HOME.pathname,
  );
  const shouldHideTossLocalMarketHeader =
    isLocalMarketPath && subDomain === SUB_DOMAIN.TOSS;

  if (isHiddenPage || shouldHideTossLocalMarketHeader) return null;

  const isSearchPage = pathname === ROUTES.SEARCH.pathname;

  const handleSearchClick = () => {
    if (!isSearchPage) {
      router.push(ROUTES.SEARCH.pathname);

      return;
    }
    (isOverlayOpen ? closeOverlay : openOverlay)();
  };

  const isHomePage = pathname === ROUTES.HOME.pathname;

  const handleHomeClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (!isHomePage) return;

    e.preventDefault();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const desktopNavItems = DESKTOP_NAV_ITEMS.filter(
    (tab) =>
      !(
        subDomain === SUB_DOMAIN.TOSS &&
        tab.href === WEL_MARKET_ROUTES.HOME.pathname
      ),
  );

  return (
    <header className="sticky top-0 z-20 w-full border-b border-Gray-200 bg-Gray-white mobile:hidden tablet:hidden">
      <nav className="mx-auto flex h-72 max-w-screen-xl items-center justify-between px-40">
        <div className="flex items-center gap-32">
          <CustomLink
            href={ROUTES.HOME.pathname}
            className="flex shrink-0 items-center"
            aria-label="Wello 홈"
            onClick={handleHomeClick}
          >
            <HeaderLogo />
          </CustomLink>
          <div className="flex items-center gap-4">
            {desktopNavItems.map((tab) => (
              <NavLink
                key={tab.href}
                href={tab.href}
                isActive={tab.isActive(pathname)}
                onClick={
                  tab.href === ROUTES.HOME.pathname
                    ? handleHomeClick
                    : undefined
                }
              >
                {tab.label}
              </NavLink>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-12">
          <button
            type="button"
            aria-label="검색"
            onClick={handleSearchClick}
            className="flex size-40 shrink-0 items-center justify-center p-0 text-Gray-700 transition-colors hover:text-Gray-900"
          >
            <SvgMagnifyingGlass className="size-24" />
          </button>
          {isLocalMarketPath && <DesktopCartButton />}
          <HeaderProfileAvatar showProfileAvatar={isLogin} />
          {isLogin === false && (
            <Button asChild dimensions="H40" roundness="capsule">
              <CustomLink href={ROUTES.LOGIN.pathname}>로그인</CustomLink>
            </Button>
          )}
        </div>
      </nav>
      <HeaderSearchContainer />
    </header>
  );
};

const DesktopCartButton = () => {
  const { totalCount } = useMarketCart();

  return (
    <CustomLink
      href={WEL_MARKET_ROUTES.CART.pathname}
      className="relative flex size-40 shrink-0 items-center justify-center p-0 text-Gray-700 transition-colors hover:text-Gray-900"
      aria-label="장바구니"
    >
      <SvgShoppingBasketFill className="size-24" />
      {totalCount > 0 && (
        <span className="absolute right-4 top-4 flex size-16 items-center justify-center rounded-pill bg-R-500 font-700 text-Gray-white typo-caption2">
          {totalCount}
        </span>
      )}
    </CustomLink>
  );
};

const NavLink = ({
  href,
  isActive,
  children,
  onClick,
}: {
  href: string;
  isActive: boolean;
  children: React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}) => (
  <CustomLink
    href={href}
    aria-current={isActive ? 'page' : undefined}
    className={cn(
      'flex h-40 items-center px-12 font-600 transition-colors typo-body2',
      isActive ? 'text-Gray-900' : 'text-Gray-500 hover:text-Gray-700',
    )}
    onClick={onClick}
  >
    {children}
  </CustomLink>
);
